import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { useDebounce } from 'use-lodash-debounce';
import { FiltersWrapper } from '../../FutureContracts/style'
import { Dropdown, Input } from 'semantic-ui-react'
import ExcelExport from '~/components/ExcelExport'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'

const defaultFilterData = {
  rows: 20,
  page: 0,
  search: '',
  articleId: '',
  month: ''
}

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Article No.'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Article name'),
    key: 'name'
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  },
  {
    name: i18n.t('Sum'),
    key: 'articlesCount'
  },
  {
    name: i18n.t('Net value of articles (in €)'),
    key: 'netTotal',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Tax (in %)'),
    key: 'tax',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Tax (in €)'),
    key: 'taxTotal',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Brutto value of articles (in €)'),
    key: 'grossTotal',
    type: 'no-unit-number'
  }
]

const getTableColumnHeadersFarmers = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName'
  },
  {
    name: i18n.t('Booking date'),
    key: 'bookingDate',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Article No.'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Article name'),
    key: 'name'
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  },
  {
    name: i18n.t('Sum'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Tax (in %)'),
    key: 'tax',
    type: 'no-unit-number'
  }
]

const ArticleBookings: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lighUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [search, setSearch] = useState('')
  const debouncedValue = useDebounce(search, 300);
  const [dataFiltered, setDataFiltered] = useState(defaultFilterData)

  const {
    data: articleSettings,
    isLoading: isBusySettings
  } = ReportingQuery.getArticleBookingsSettings()

  const {
    data: articleBookingsData,
    isLoading: isBusyArticleBokings,
    isFetching: isFetchingArticleBookings,
    refetch: fetchArticleBookings
  } = ReportingQuery.getArticleBookings({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: farmersData,
    isLoading: isBusyFarmers,
    isFetching: isFetchingFarmers,
    refetch: fetchFarmers
  } = ReportingQuery.getArticleBookingFarmers({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  useEffect(() => {
    fetchArticleBookings()
    fetchFarmers()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function renderFilters() {
    return <FiltersWrapper>
      <Dropdown
        selection
        clearable
        selectOnBlur={false}
        placeholder={t('Select procurement month')}
        value={dataFiltered.month}
        options={articleSettings?.months?.map(month => ({ value: month?.key, text: month?.label })) || []}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, month: data.value as string })}
      />
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Farmer search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selection
        selectOnBlur={false}
        search
        clearable
        placeholder={t('Select article')}
        value={dataFiltered.articleId}
        options={articleSettings?.articles?.map(article => ({ value: article?.id, text: `${article?.name} (${article?.uniqueId})` })) || []}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, articleId: data.value as string })}
      />
    </FiltersWrapper>
  }

  function renderSumTable() {
    return <TableComponent
      tableColumnHeaders={getTableColumnHeaders()}
      removePagination
      animated
      isFetchingData={isFetchingArticleBookings}
      rows={articleBookingsData || []}
      total={articleBookingsData?.length || 0}
    />
  }

  function renderFarmersTable() {
    return <TableComponent
      tableColumnHeaders={getTableColumnHeadersFarmers()}
      animated
      isFetchingData={isFetchingFarmers}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(dataFiltered) => setDataFiltered(dataFiltered)}
      rows={rows}
      total={filtered}
    />
  }

  function renderActions() {
    return <>
      <ExcelExport
        url={'reporting/article-bookings/export/excel'}
        name={t('Excel export')}
        fileName={'article-bookings'}
        data={{ ...dataFiltered }}
      />
      <ExcelExport
        name={t('PDF')}
        url={'reporting/article-bookings/export/pdf'}
        fileName={'article-bookings-pdf'}
        data={{ ...dataFiltered }} />
    </>
  }

  const { filtered, rows } = farmersData ? farmersData : { filtered: 0, rows: [] }

  const loading = isBusyArticleBokings || isBusyFarmers || isBusySettings

  return <Layout
    title={t('Article bookings')}
    segmented
    isBusy={loading}
    filters={renderFilters()}
    actions={!lighUserLogged && renderActions()}
  >
    {renderSumTable()}
    <div style={{ marginTop: '2em' }}>
      {renderFarmersTable()}
    </div>
  </Layout>
}
export default ArticleBookings